import React, { useState, useCallback } from "react";
import { T, Btn, Badge, Modal, Field, Input, Select, SectionLabel } from "../shared/UI";
import { STATUS, fmt } from "../../utils/helpers";
import { useApp } from "../../store/AppContext";

// ─────────────────────────────────────────────────────────────
//  AgentsView — main container
// ─────────────────────────────────────────────────────────────
export default function AgentsView({
  agents, zones,
  createAgent, updateAgent, updatePassword, updateStatus, updateLocationPerm,
  notify,
}) {
  const { openAlertModal, openHistory, setView, openUsersModal } = useApp();
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState("all");
  const [modal,   setModal]   = useState(null); // { mode:"create"|"edit"|"password", agent? }
  const [confirm, setConfirm] = useState(null); // { agent, action: "delete"|"toggle"|"location" }

  const filtered = agents.filter(a => {
    const ms = filter === "all" || a.status === filter || (filter === "agent" && a.role === "AGENT");
    const mq = !search || a.name?.toLowerCase().includes(search.toLowerCase())
      || a.mobile?.includes(search) || a.city?.toLowerCase().includes(search.toLowerCase());
    return ms && mq;
  });

  // ── Handlers ─────────────────────────────────────────────────
  const handleCreate = async (data) => {
    try {
      await createAgent(data);
      notify("success", "Agent created successfully");
      setModal(null);
    } catch (e) { notify("error", e.message || "Failed to create agent"); }
  };

  const handleUpdate = async (data) => {
    try {
      await updateAgent(modal.agent.id, data);
      notify("success", "Agent updated");
      setModal(null);
    } catch (e) { notify("error", e.message || "Failed to update agent"); }
  };

  const handlePassword = async (password) => {
    try {
      await updatePassword(modal.agent.id, password);
      notify("success", "Password updated");
      setModal(null);
    } catch (e) { notify("error", e.message || "Failed to update password"); }
  };

  const handleToggleStatus = async (agent) => {
    const newStatus = agent.status === "active" ? "off" : "on";
    try {
      await updateStatus(agent.id, newStatus);
      notify("success", `Agent ${newStatus === "on" ? "activated" : "deactivated"}`);
    } catch (e) { notify("error", e.message || "Failed to update status"); }
  };

  const handleToggleLocation = async (agent) => {
    const newVal = agent.allowAnyLocation === 1 ? 0 : 1;
    try {
      await updateLocationPerm(agent.id, newVal);
      notify("success", newVal === 1 ? "Location restriction removed" : "Location restriction enabled");
    } catch (e) { notify("error", e.message || "Failed to update permission"); }
  };

  return (
    <div style={styles.wrap}>
      {/* ── Toolbar ── */}
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <span style={{ color: T.muted, fontSize: 16 }}>⌕</span>
          <input
            style={styles.searchInput}
            placeholder="Search by name, mobile, city…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button style={styles.clearBtn} onClick={() => setSearch("")}>×</button>}
        </div>

        <div style={styles.filterRow}>
          {[
            { id:"all",     label:"ALL" },
            { id:"active",  label:"ACTIVE" },
            { id:"offline", label:"OFFLINE" },
          ].map(f => (
            <button key={f.id}
              style={{ ...styles.fBtn, ...(filter === f.id ? styles.fBtnActive : {}) }}
              onClick={() => setFilter(f.id)}>
              {f.label}
              <span style={styles.fCount}>
                {f.id === "all"     ? agents.length :
                 f.id === "active"  ? agents.filter(a => a.status === "active").length :
                                      agents.filter(a => a.status === "offline").length}
              </span>
            </button>
          ))}
        </div>

        <Btn variant="primary" icon="+" onClick={() => { setView("users"); openUsersModal("create"); }}>
          Add Agent
        </Btn>
      </div>

      {/* ── Stats row ── */}
      <div style={styles.statsRow}>
        {[
          { label: "Total Agents",  val: agents.length,                                       color: T.acc  },
          { label: "Active",        val: agents.filter(a => a.status === "active").length,    color: T.acc3 },
          { label: "Offline",       val: agents.filter(a => a.status === "offline").length,   color: T.muted },
          { label: "Location Free", val: agents.filter(a => a.allowAnyLocation === 1).length, color: T.acc4 },
        ].map(s => (
          <div key={s.label} style={styles.statCard}>
            <div style={{ fontFamily: T.fontMono, fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Agent grid ── */}
      <div style={styles.grid}>
        {filtered.map(agent => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit={()     => { setView("users"); openUsersModal("edit", agent); }}
            onPassword={()  => setModal({ mode: "password", agent })}
            onToggleStatus={() => handleToggleStatus(agent)}
            onToggleLocation={() => handleToggleLocation(agent)}
            onAlert={()    => openAlertModal(agent)}
            onHistory={()  => openHistory(agent)}
            notify={notify}
          />
        ))}
        {filtered.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 32 }}>👥</div>
            <div style={{ color: T.muted, marginTop: 8 }}>No agents match your filter</div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <CreateEditModal
        open={modal?.mode === "create" || modal?.mode === "edit"}
        mode={modal?.mode}
        agent={modal?.agent}
        onSave={modal?.mode === "create" ? handleCreate : handleUpdate}
        onClose={() => setModal(null)}
      />
      <PasswordModal
        open={modal?.mode === "password"}
        agent={modal?.agent}
        onSave={handlePassword}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Agent Card
// ─────────────────────────────────────────────────────────────
function AgentCard({ agent, onEdit, onPassword, onToggleStatus, onToggleLocation, onAlert, onHistory }) {
  const s = STATUS[agent.status] || STATUS.offline;
  const isActive = agent.status === "active";

  return (
    <div style={{ ...styles.card, ...(agent.status === "sos" ? styles.sosCard : {}) }}>
      {/* SOS banner */}
      {agent.status === "sos" && (
        <div style={styles.sosBanner}>🚨 SOS ACTIVE — IMMEDIATE ATTENTION REQUIRED</div>
      )}

      {/* Header row */}
      <div style={styles.cardTop}>
        <div style={{ ...styles.avatar, background: s.bg, border: `2px solid ${s.color}`, color: s.color }}>
          {agent.initials}
          <div style={{ ...styles.statusDot, background: s.color }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 2 }}>{agent.name}</div>
          <div style={{ fontSize: 11, color: T.muted }}>{agent.email}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <Badge color={s.color} bg={s.bg} border={s.border}>● {s.label}</Badge>
            {agent.city && <Badge color={T.acc} bg="rgba(124,106,247,0.08)" border="rgba(124,106,247,0.2)">{agent.city}</Badge>}
            {agent.allowAnyLocation === 1
              ? <Badge color={T.acc3} bg="rgba(106,247,196,0.08)" border="rgba(106,247,196,0.2)">📍 Any Location</Badge>
              : <Badge color={T.muted} bg="rgba(77,102,128,0.08)" border="rgba(77,102,128,0.2)">📍 Restricted</Badge>
            }
          </div>
        </div>

        {/* Edit & password icons */}
        <div style={{ display: "flex", gap: 4, alignSelf: "flex-start" }}>
          <button style={styles.iconBtn} onClick={onEdit}     title="Edit profile">✏️</button>
          <button style={styles.iconBtn} onClick={onPassword} title="Change password">🔑</button>
        </div>
      </div>

      {/* Info grid */}
      <div style={styles.infoGrid}>
        <InfoRow icon="📱" val={agent.mobile} />
        {agent.productName && <InfoRow icon="📦" val={agent.productName} />}
        {agent.alternateMobile && <InfoRow icon="📞" val={agent.alternateMobile} />}
        <InfoRow icon="🏙" val={`${agent.city || "—"}${agent.pincode ? " · " + agent.pincode : ""}`} />
        {agent.address && <InfoRow icon="📍" val={agent.address} />}
        {agent.deviceId && <InfoRow icon="📡" val={`Device #${agent.deviceId}`} />}
        {agent.lat && agent.lng && <InfoRow icon="🛰" val={`${Number(agent.lat).toFixed(5)}°N, ${Number(agent.lng).toFixed(5)}°E`} />}
      </div>

      {/* Live metrics (if reporting) */}
      {agent.isReporting === 1 && (
        <div style={styles.liveMetrics}>
          <div style={styles.liveTag}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.acc3, animation: "pulse 1.5s infinite" }} />
            LIVE
          </div>
          <MetricPill label="SPD" val={`${agent.speed || 0} km/h`} />
          <MetricPill label="HDG" val={`${agent.heading || 0}°`} />
          <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>
            {agent.timestamp || agent.lastSeen}
          </div>
        </div>
      )}

      {/* Divider */}
      <div style={styles.divider} />

      {/* Toggle controls */}
      <div style={styles.toggleRow}>
        <ToggleSwitch
          label="Agent Active"
          hint={isActive ? "Tap to deactivate" : "Tap to activate"}
          checked={isActive}
          color={T.acc3}
          onChange={onToggleStatus}
        />
        <ToggleSwitch
          label="Any Location"
          hint={agent.allowAnyLocation === 1 ? "Location unrestricted" : "Location restricted"}
          checked={agent.allowAnyLocation === 1}
          color={T.acc4}
          onChange={onToggleLocation}
        />
      </div>

      {/* Action buttons */}
      <div style={styles.cardActions}>
        <Btn size="sm" variant="outline"  onClick={onAlert}>💬 Send Alert</Btn>
        <Btn size="sm" variant="ghost"    onClick={onHistory}>⏱ Play History</Btn>
        {agent.status === "sos" && <Btn size="sm" variant="danger">🚨 Respond SOS</Btn>}
      </div>
    </div>
  );
}

function InfoRow({ icon, val }) {
  if (!val || val === "—") return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <span style={{ fontSize: 12, color: T.muted, lineHeight: 1.4, wordBreak: "break-all" }}>{val}</span>
    </div>
  );
}

function MetricPill({ label, val }) {
  return (
    <div style={{ background: T.s3, borderRadius: 5, padding: "3px 8px", display: "flex", gap: 4 }}>
      <span style={{ fontSize: 9, color: T.muted, fontFamily: T.fontMono }}>{label}</span>
      <span style={{ fontSize: 10, color: T.text, fontFamily: T.fontMono, fontWeight: 600 }}>{val}</span>
    </div>
  );
}

function ToggleSwitch({ label, hint, checked, color, onChange }) {
  return (
    <div style={styles.toggleItem} onClick={onChange}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{label}</div>
        <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{hint}</div>
      </div>
      <div style={{ ...styles.toggleTrack, background: checked ? `${color}22` : T.s3, borderColor: checked ? color : T.border2 }}>
        <div style={{ ...styles.toggleThumb, background: checked ? color : T.muted, transform: checked ? "translateX(18px)" : "translateX(2px)" }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Create / Edit Modal
// ─────────────────────────────────────────────────────────────
function CreateEditModal({ open, mode, agent, onSave, onClose }) {
  const isEdit = mode === "edit";
  const init = {
    fullName: "", email: "", mobile: "", password: "",
    alternateMobile: "", city: "", pincode: "", address: "",
    latitude: "", longitude: "", role: "AGENT",
  };
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setErrors({});
      setForm(isEdit && agent ? {
        fullName:        agent.fullName || agent.name || "",
        email:           agent.email || "",
        mobile:          agent.mobile || "",
        password:        "",
        alternateMobile: agent.alternateMobile || "",
        city:            agent.city || "",
        pincode:         agent.pincode || "",
        address:         agent.address || "",
        latitude:        agent.lat ? String(agent.lat) : "",
        longitude:       agent.lng ? String(agent.lng) : "",
        role:            agent.role || "AGENT",
      } : init);
    }
  }, [open, agent, isEdit]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim())                  e.fullName = "Full name required";
    if (!isEdit && !form.email.trim())          e.email    = "Email required";
    if (!isEdit && !form.mobile.trim())         e.mobile   = "Mobile required";
    if (!isEdit && form.mobile.length !== 10)   e.mobile   = "Must be 10 digits";
    if (!isEdit && !form.password.trim())       e.password = "Password required";
    if (!isEdit && form.password.length < 6)    e.password = "Min 6 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Agent Profile" : "Add New Agent"} width={560}>
      <div style={styles.formGrid}>
        {/* Full name */}
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="FULL NAME" error={errors.fullName}>
            <Input value={form.fullName} onChange={set("fullName")} placeholder="John Doe" />
          </Field>
        </div>

        {/* Email */}
        <Field label="EMAIL" error={errors.email}>
          <Input value={form.email} onChange={set("email")} placeholder="agent@example.com" type="email"
            style={{ opacity: isEdit ? 0.5 : 1 }} />
        </Field>

        {/* Mobile */}
        <Field label="MOBILE" error={errors.mobile}>
          <Input value={form.mobile} onChange={e => set("mobile")({ target: { value: e.target.value.replace(/\D/g,"").slice(0,10) }})}
            placeholder="9876543210" style={{ opacity: isEdit ? 0.5 : 1 }} />
        </Field>

        {/* Alternate mobile */}
        <Field label="ALTERNATE MOBILE">
          <Input value={form.alternateMobile} onChange={set("alternateMobile")} placeholder="9876501234 (optional)" />
        </Field>

        {/* Password (create only) */}
        {!isEdit && (
          <Field label="PASSWORD" error={errors.password}>
            <Input value={form.password} onChange={set("password")} type="password" placeholder="Min 6 characters" />
          </Field>
        )}

        {/* City */}
        <Field label="CITY">
          <Input value={form.city} onChange={set("city")} placeholder="Mumbai" />
        </Field>

        {/* Pincode */}
        <Field label="PINCODE">
          <Input value={form.pincode} onChange={e => set("pincode")({ target: { value: e.target.value.replace(/\D/g,"").slice(0,6) }})}
            placeholder="400001" />
        </Field>

        {/* Address */}
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="ADDRESS">
            <Input value={form.address} onChange={set("address")} placeholder="Street address, area…" />
          </Field>
        </div>

        {/* Lat / Lng */}
        <Field label="LATITUDE">
          <Input value={form.latitude} onChange={set("latitude")} placeholder="19.0760 (optional)" />
        </Field>
        <Field label="LONGITUDE">
          <Input value={form.longitude} onChange={set("longitude")} placeholder="72.8777 (optional)" />
        </Field>
      </div>

      {/* Info note for edit */}
      {isEdit && (
        <div style={styles.infoNote}>
          ℹ To change mobile/email, contact the system administrator. Use the 🔑 button on the card to update password.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <Btn variant="ghost"   full onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" full onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Agent"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
//  Password Modal
// ─────────────────────────────────────────────────────────────
function PasswordModal({ open, agent, onSave, onClose }) {
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [showPass,  setShowPass]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  React.useEffect(() => {
    if (open) { setPassword(""); setConfirm(""); setError(""); }
  }, [open]);

  const handleSave = async () => {
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm)  { setError("Passwords do not match"); return; }
    setSaving(true);
    try { await onSave(password); }
    catch (e) { setError(e.message || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Change Password — ${agent?.name || ""}`} width={400}>
      <div style={{ marginBottom: 8, fontSize: 13, color: T.muted }}>
        Set a new password for this agent's mobile app login.
      </div>

      <Field label="NEW PASSWORD">
        <div style={{ position: "relative" }}>
          <Input value={password} onChange={e => setPassword(e.target.value)}
            type={showPass ? "text" : "password"} placeholder="Min 6 characters"
            style={{ paddingRight: 40 }} />
          <button onClick={() => setShowPass(v => !v)}
            style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:14 }}>
            {showPass ? "🙈" : "👁"}
          </button>
        </div>
      </Field>

      <Field label="CONFIRM PASSWORD">
        <Input value={confirm} onChange={e => setConfirm(e.target.value)}
          type={showPass ? "text" : "password"} placeholder="Re-enter password" />
      </Field>

      {/* Strength meter */}
      {password.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, marginBottom: 4 }}>
            STRENGTH: {password.length < 6 ? "WEAK" : password.length < 10 ? "FAIR" : "STRONG"}
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2,
                background: i === 0 ? (password.length >= 1 ? T.sos   : T.border2)
                           : i === 1 ? (password.length >= 6 ? T.acc4  : T.border2)
                           :            (password.length >= 10 ? T.acc3 : T.border2) }} />
            ))}
          </div>
        </div>
      )}

      {error && <div style={{ color: T.sos, fontSize: 12, marginBottom: 12 }}>⚠ {error}</div>}

      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="ghost"   full onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" full onClick={handleSave} disabled={saving}>
          {saving ? "Updating…" : "Update Password"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
//  Styles
// ─────────────────────────────────────────────────────────────
const styles = {
  wrap:        { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  toolbar:     { display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, flexWrap: "wrap" },
  searchWrap:  { display: "flex", alignItems: "center", gap: 8, background: T.s2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "7px 12px", flex: 1, maxWidth: 320, position: "relative" },
  searchInput: { background: "none", border: "none", outline: "none", color: T.text, fontFamily: T.fontSans, fontSize: 13, width: "100%" },
  clearBtn:    { background: "none", border: "none", color: T.muted, cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 },
  filterRow:   { display: "flex", gap: 4 },
  fBtn:        { display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: "none", color: T.muted, fontFamily: T.fontMono, fontSize: 9, cursor: "pointer", letterSpacing: "0.5px", transition: "all 0.18s" },
  fBtnActive:  { borderColor: T.acc, color: T.acc, background: "rgba(124,106,247,0.08)" },
  fCount:      { background: T.s3, borderRadius: 10, padding: "1px 6px", fontSize: 9, color: T.muted },
  statsRow:    { display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0 },
  statCard:    { flex: 1, padding: "12px 20px", borderRight: `1px solid ${T.border}` },
  grid:        { flex: 1, overflowY: "auto", padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px,1fr))", gap: 14, alignContent: "start" },
  empty:       { gridColumn: "1/-1", textAlign: "center", padding: "60px 20px", color: T.muted },
  card:        { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, transition: "border-color 0.2s", display: "flex", flexDirection: "column", gap: 0 },
  sosCard:     { borderColor: "#ff4466", boxShadow: "0 0 20px rgba(255,68,102,0.2)" },
  sosBanner:   { background: "rgba(255,68,102,0.15)", border: "1px solid rgba(255,68,102,0.3)", borderRadius: 6, padding: "6px 10px", fontSize: 11, fontFamily: T.fontMono, color: "#ff4466", marginBottom: 12, textAlign: "center", animation: "pulse 1.5s infinite" },
  cardTop:     { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 },
  avatar:      { width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, fontFamily: T.fontMono, flexShrink: 0, position: "relative" },
  statusDot:   { position: "absolute", bottom: -2, right: -2, width: 11, height: 11, borderRadius: "50%", border: `2px solid ${T.s2}` },
  infoGrid:    { marginBottom: 10 },
  liveMetrics: { display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" },
  liveTag:     { display: "flex", alignItems: "center", gap: 4, background: "rgba(106,247,196,0.1)", border: "1px solid rgba(106,247,196,0.2)", borderRadius: 20, padding: "2px 8px", fontFamily: T.fontMono, fontSize: 9, color: T.acc3, letterSpacing: 1 },
  divider:     { height: 1, background: T.border, margin: "10px 0" },
  toggleRow:   { display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 },
  toggleItem:  { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: T.s3, borderRadius: 8, cursor: "pointer", transition: "background 0.18s" },
  toggleTrack: { width: 38, height: 22, borderRadius: 11, border: "1.5px solid", position: "relative", transition: "all 0.25s", flexShrink: 0 },
  toggleThumb: { position: "absolute", top: 3, width: 14, height: 14, borderRadius: "50%", transition: "transform 0.25s, background 0.25s" },
  cardActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  iconBtn:     { background: T.s3, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 13, transition: "all 0.18s" },
  infoNote:    { background: "rgba(124,106,247,0.08)", border: `1px solid rgba(124,106,247,0.2)`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: T.muted, marginTop: 4 },
  formGrid:    { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" },
};
